// Central bank gold reserves: browser-safe model and pure cleaner.
// Source: IMF International Reserves and Foreign Currency Liquidity (IRFCL),
// monthly, reported by national authorities. The same cleaner runs on the
// live server read and on the stored snapshot so both always agree.

export const OZ_PER_TONNE = 32150.7466;

export const IND_VOLUME = "IRFCLDT1_IRFCL56V_FTO"; // gold, fine troy ounces
export const IND_GOLD_USD = "IRFCLDT1_IRFCL56_USD"; // gold, USD value
export const IND_RESERVES_USD = "IRFCLDT1_IRFCL65_USD"; // official reserve assets, USD

// Aggregates that would double count national holdings.
const AGGREGATES = new Set(["G163"]);

// Reporters that carry gold at a statutory or historical cost price instead of
// market value. Their volume is valid but the reported USD value is not.
const BOOK_VALUE = new Set(["USA", "SAU", "SGP", "KOR"]);

// Preferred sector when a country files under more than one.
const SECTOR_ORDER = ["S1XS1311", "S1X", "S1311"];

// No single holder reports more than this in ounces (the US holds ~261m oz),
// so larger values are filed in the wrong unit scale (thousands).
const MAX_PLAUSIBLE_OZ = 5e8;

export const COUNTRY_NAMES: Record<string, string> = {
  AGO: "Angola", ALB: "Albania", ARG: "Argentina", ARM: "Armenia", AUS: "Australia", AUT: "Austria",
  BEL: "Belgium", BGR: "Bulgaria", BIH: "Bosnia and Herzegovina", BLR: "Belarus", BRA: "Brazil",
  CAN: "Canada", CHE: "Switzerland", CHL: "Chile", CHN: "China", COL: "Colombia", CRI: "Costa Rica",
  CYP: "Cyprus", CZE: "Czech Republic", DEU: "Germany", DNK: "Denmark", DOM: "Dominican Republic",
  ECU: "Ecuador", EGY: "Egypt", ESP: "Spain", EST: "Estonia", EZB: "European Central Bank", FIN: "Finland",
  FRA: "France", GBR: "United Kingdom", GEO: "Georgia", GRC: "Greece", GTM: "Guatemala", HKG: "Hong Kong SAR",
  HND: "Honduras", HRV: "Croatia", HUN: "Hungary", IDN: "Indonesia", IND: "India", IRL: "Ireland",
  ISL: "Iceland", ISR: "Israel", ITA: "Italy", JAM: "Jamaica", JOR: "Jordan", JPN: "Japan",
  KAZ: "Kazakhstan", KGZ: "Kyrgyz Republic", KHM: "Cambodia", KOR: "South Korea", KOS: "Kosovo",
  LKA: "Sri Lanka", LTU: "Lithuania", LUX: "Luxembourg", LVA: "Latvia", MAR: "Morocco", MDA: "Moldova",
  MEX: "Mexico", MKD: "North Macedonia", MLT: "Malta", MNG: "Mongolia", MUS: "Mauritius", MYS: "Malaysia",
  NIC: "Nicaragua", NLD: "Netherlands", NOR: "Norway", NZL: "New Zealand", PER: "Peru", PHL: "Philippines",
  POL: "Poland", PRT: "Portugal", PRY: "Paraguay", ROU: "Romania", RUS: "Russia", RWA: "Rwanda",
  SAU: "Saudi Arabia", SGP: "Singapore", SLV: "El Salvador", SRB: "Serbia", SVK: "Slovakia", SVN: "Slovenia",
  SWE: "Sweden", THA: "Thailand", TUR: "Turkey", UKR: "Ukraine", URY: "Uruguay", USA: "United States",
  WBG: "West Bank and Gaza", ZAF: "South Africa",
};

export type ImfObservation = {
  country: string;
  indicator: string;
  sector: string;
  period: string; // YYYY-MM
  value: number;
};

export type Valuation = "market" | "book" | "unverified";

export type CbgCountry = {
  code: string;
  name: string;
  tonnes: number;
  asOf: string; // YYYY-MM
  m1: number | null; // tonnes change vs previous month
  m12: number | null; // tonnes change vs same month a year earlier
  ytd: number | null; // tonnes change vs December of the previous year
  goldShare: number | null; // gold at market value as % of official reserves
  valuation: Valuation;
  stale: boolean; // latest report older than 6 months behind the dataset
  series: [string, number][]; // last 24 monthly readings, tonnes
};

export type CbgDTO = {
  countries: CbgCountry[]; // sorted by tonnes, largest first
  latestPeriod: string;
  marketPrice: number | null; // median implied USD/oz in the latest period
  totals: {
    tonnes: number;
    m12Net: number;
    buyers12: number;
    sellers12: number;
    reporting: number;
  };
  generatedAt: string;
  source: "live" | "snapshot";
};

/** IMF period "2026-M09" to "2026-09". */
export function normalizePeriod(p: string): string | null {
  const m = /^(\d{4})-?M?(\d{2})$/.exec(p.trim());
  return m ? `${m[1]}-${m[2]}` : null;
}

export function shiftMonth(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  const idx = y * 12 + (m - 1) + delta;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

export function monthsBetween(a: string, b: string): number {
  const [ay, am] = a.split("-").map(Number);
  const [by, bm] = b.split("-").map(Number);
  return by * 12 + bm - (ay * 12 + am);
}

const round1 = (x: number) => Math.round(x * 10) / 10;

function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Minimal CSV reader for the IMF SDMX CSV (handles quoted fields). */
export function parseImfCsv(text: string): ImfObservation[] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.length > 1) rows.push(row);
      row = [];
    } else field += c;
  }
  if (field || row.length) { row.push(field); if (row.length > 1) rows.push(row); }
  const header = rows.shift() ?? [];
  const col = (name: string) => header.indexOf(name);
  const iC = col("COUNTRY"), iI = col("INDICATOR"), iS = col("SECTOR"), iT = col("TIME_PERIOD"), iV = col("OBS_VALUE");
  if ([iC, iI, iS, iT, iV].some((i) => i < 0)) throw new Error("Unexpected IMF CSV layout");
  const out: ImfObservation[] = [];
  for (const r of rows) {
    const period = normalizePeriod(r[iT] ?? "");
    const value = Number(r[iV]);
    if (!period || r[iV] === "" || !Number.isFinite(value)) continue;
    out.push({ country: r[iC], indicator: r[iI], sector: r[iS], period, value });
  }
  return out;
}

/**
 * Turn raw IMF observations into the published dataset. Rules, in order:
 * 1. Drop aggregates and keep one sector per country.
 * 2. Fix ounce values filed a thousand times too large.
 * 3. Cross check volume against reported USD value at the market price. Book
 *    value reporters keep their volume; inconsistent filers are excluded.
 */
export function buildCentralBankGold(obs: ImfObservation[], generatedAt: string, source: CbgDTO["source"]): CbgDTO {
  type Series = Map<string, number>;
  const byCountry = new Map<string, Map<string, { vol: Series; usd: Series; res: Series }>>();
  for (const o of obs) {
    if (AGGREGATES.has(o.country) || !/^[A-Z]{3}$/.test(o.country)) continue;
    let sectors = byCountry.get(o.country);
    if (!sectors) byCountry.set(o.country, (sectors = new Map()));
    let s = sectors.get(o.sector);
    if (!s) sectors.set(o.sector, (s = { vol: new Map(), usd: new Map(), res: new Map() }));
    if (o.indicator === IND_VOLUME) s.vol.set(o.period, o.value > MAX_PLAUSIBLE_OZ ? o.value / 1000 : o.value);
    else if (o.indicator === IND_GOLD_USD) s.usd.set(o.period, o.value);
    else if (o.indicator === IND_RESERVES_USD) s.res.set(o.period, o.value);
  }

  const picked = new Map<string, { vol: Series; usd: Series; res: Series }>();
  for (const [code, sectors] of byCountry) {
    const key = SECTOR_ORDER.find((k) => (sectors.get(k)?.vol.size ?? 0) > 0) ??
      [...sectors.keys()].find((k) => (sectors.get(k)?.vol.size ?? 0) > 0);
    if (key) picked.set(code, sectors.get(key)!);
  }

  // Market price per month: median implied USD/oz across market value reporters.
  const implied = new Map<string, number[]>();
  for (const [code, s] of picked) {
    if (BOOK_VALUE.has(code)) continue;
    for (const [p, oz] of s.vol) {
      const usd = s.usd.get(p);
      if (usd && oz > 0) {
        const px = usd / oz;
        if (px > 300 && px < 50000) (implied.get(p) ?? implied.set(p, []).get(p)!).push(px);
      }
    }
  }
  const market = new Map<string, number>();
  for (const [p, xs] of implied) if (xs.length >= 5) market.set(p, median(xs)!);

  const latestPeriod = [...picked.values()].flatMap((s) => [...s.vol.keys()]).sort().at(-1) ?? "";
  const countries: CbgCountry[] = [];

  for (const [code, s] of picked) {
    const periods = [...s.vol.keys()].sort();
    const asOf = periods.at(-1)!;
    const oz = s.vol.get(asOf)!;
    if (!(oz > 0)) continue;

    let valuation: Valuation = "unverified";
    const both = periods.filter((p) => s.usd.has(p) && market.has(p));
    if (both.length) {
      const p = both.at(-1)!;
      const ratio = s.usd.get(p)! / s.vol.get(p)! / market.get(p)!;
      if (ratio > 0.75 && ratio < 1.33) valuation = "market";
      else if (BOOK_VALUE.has(code)) valuation = "book";
      else continue; // volume and value disagree: unit error at source
    } else if (s.usd.size) {
      const p = [...s.usd.keys()].filter((k) => market.has(k)).sort().at(-1);
      if (p) {
        const nearVol = s.vol.get(periods.filter((k) => k <= p).at(-1) ?? asOf)!;
        const ratio = s.usd.get(p)! / market.get(p)! / nearVol;
        if (ratio > 0.75 && ratio < 1.33) valuation = "market";
        else if (BOOK_VALUE.has(code)) valuation = "book";
        else continue;
      }
    } else if (BOOK_VALUE.has(code)) valuation = "book";

    const t = (p: string) => {
      const v = s.vol.get(p);
      return v == null ? null : v / OZ_PER_TONNE;
    };
    const tonnes = oz / OZ_PER_TONNE;
    const diff = (p: string) => {
      const prev = t(p);
      return prev == null ? null : round1(tonnes - prev);
    };

    // Gold share of official reserves, with gold restated at market value.
    let goldShare: number | null = null;
    const shareP = periods.filter((p) => s.res.has(p) && market.has(p) && (valuation !== "market" || s.usd.has(p))).at(-1);
    if (shareP) {
      const goldMkt = s.vol.get(shareP)! * market.get(shareP)!;
      const goldBook = s.usd.get(shareP) ?? 0;
      const total = valuation === "market" ? s.res.get(shareP)! : s.res.get(shareP)! - goldBook + goldMkt;
      const gold = valuation === "market" ? s.usd.get(shareP)! : goldMkt;
      if (total > 0 && gold <= total * 1.001) goldShare = round1((gold / total) * 100);
    }

    countries.push({
      code,
      name: COUNTRY_NAMES[code] ?? code,
      tonnes: round1(tonnes),
      asOf,
      m1: diff(shiftMonth(asOf, -1)),
      m12: diff(shiftMonth(asOf, -12)),
      ytd: asOf.endsWith("-12") ? diff(shiftMonth(asOf, -12)) : diff(`${Number(asOf.slice(0, 4)) - 1}-12`),
      goldShare,
      valuation,
      stale: latestPeriod ? monthsBetween(asOf, latestPeriod) > 6 : false,
      series: periods.slice(-24).map((p) => [p, round1(s.vol.get(p)! / OZ_PER_TONNE)] as [string, number]),
    });
  }

  countries.sort((a, b) => b.tonnes - a.tonnes);
  const fresh = countries.filter((c) => !c.stale);
  return {
    countries,
    latestPeriod,
    marketPrice: market.get(latestPeriod) ?? null,
    totals: {
      tonnes: round1(countries.reduce((a, c) => a + c.tonnes, 0)),
      m12Net: round1(fresh.reduce((a, c) => a + (c.m12 ?? 0), 0)),
      buyers12: fresh.filter((c) => (c.m12 ?? 0) >= 0.5).length,
      sellers12: fresh.filter((c) => (c.m12 ?? 0) <= -0.5).length,
      reporting: countries.length,
    },
    generatedAt,
    source,
  };
}

/** Dataset is usable when it covers the major reporters. */
export function isUsableDataset(d: CbgDTO): boolean {
  const codes = new Set(d.countries.map((c) => c.code));
  return d.countries.length >= 40 && codes.has("USA") && codes.has("CHN") && codes.has("DEU");
}

export function imfStartPeriod(now: Date): string {
  return `${now.getUTCFullYear() - 2}-01`;
}

export function imfDataUrl(now: Date): string {
  const ind = [IND_VOLUME, IND_GOLD_USD, IND_RESERVES_USD].join("+");
  return `https://api.imf.org/external/sdmx/2.1/data/IMF.STA,IRFCL/.${ind}..M?startPeriod=${imfStartPeriod(now)}&detail=dataonly`;
}

export const IMF_SOURCE_URL = "https://data.imf.org/en/datasets/IMF.STA:IRFCL";

export function fmtPeriod(p: string): string {
  const [y, m] = p.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}
